// ════════════════════════════════════════════════════════════════════════════
// Beskar Jobs Widget — Premium Styles
// Defense-tech aesthetic. Industrial precision meets modern luxury.
// ════════════════════════════════════════════════════════════════════════════

export function buildStyles(): string {
  return `
    @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Outfit:wght@400;500;600;700;800;900&display=swap');

    :host {
      --brand: var(--beskar-brand, #14b8a6);
      --bg: var(--beskar-bg, #0a0c10);
      --surface: var(--beskar-surface, #111318);
      --surface2: var(--beskar-surface2, #181b22);
      --surface3: var(--beskar-surface3, #1f222b);
      --border: var(--beskar-border, rgba(255,255,255,0.06));
      --border-mid: var(--beskar-border-mid, rgba(255,255,255,0.1));
      --text: var(--beskar-text, #eae8ed);
      --dim: var(--beskar-dim, #9e99a7);
      --muted: var(--beskar-muted, #5e586a);
      --teal: var(--beskar-teal, #14b8a6);
      --pink: var(--beskar-pink, #c084fc);
      --green: var(--beskar-green, #34d399);
      --amber: var(--beskar-amber, #fbbf24);
      --red: var(--beskar-red, #f87171);
      --mono: 'JetBrains Mono', monospace;
      --display: 'Outfit', sans-serif;
      --sans: 'Outfit', system-ui, sans-serif;
      --radius: var(--beskar-radius, 6px);
      --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
      --ease-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);

      display: block;
      font-family: var(--sans);
      color: var(--text);
      line-height: 1.5;
      background: var(--bg);
      min-height: 100vh;
      -webkit-font-smoothing: antialiased;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    .wrapper {
      max-width: 800px;
      margin: 0 auto;
      padding: 48px 24px;
    }

    /* ── Loading ── */
    .loading {
      display: flex; align-items: center; justify-content: center;
      padding: 120px 0; gap: 12px; color: var(--dim);
      font-family: var(--mono); font-size: 11px; letter-spacing: 0.08em;
    }
    .spin {
      width: 14px; height: 14px; border: 1.5px solid var(--border-mid);
      border-top-color: var(--brand); border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── Header ── */
    .header {
      display: flex; align-items: center; gap: 14px;
      padding-bottom: 32px;
      border-bottom: 1px solid var(--border);
      margin-bottom: 32px;
    }
    .header-logo {
      height: 36px; width: auto; border-radius: 4px;
      filter: brightness(1.05);
    }
    .header-text { flex: 1; }
    .header-name {
      font-family: var(--display); font-size: 20px; font-weight: 800;
      color: var(--text); letter-spacing: -0.03em;
    }
    .header-tagline {
      font-family: var(--mono); font-size: 10px; color: var(--muted);
      letter-spacing: 0.06em; margin-top: 2px;
    }
    .header-badge {
      font-family: var(--mono); font-size: 8px; letter-spacing: 0.14em;
      color: var(--teal); padding: 4px 10px; border-radius: 3px;
      border: 1px solid rgba(20,184,166,0.2);
      background: rgba(20,184,166,0.06);
      text-transform: uppercase; white-space: nowrap;
    }

    /* ── Search + Filters ── */
    .filters {
      display: flex; gap: 10px; margin-bottom: 24px;
    }
    .filter-input {
      flex: 1; padding: 12px 16px;
      background: var(--surface); border: 1px solid var(--border);
      border-radius: var(--radius); color: var(--text);
      font-family: var(--sans); font-size: 14px; outline: none;
      transition: border-color 200ms var(--ease-out), box-shadow 200ms var(--ease-out);
    }
    .filter-input:focus {
      border-color: var(--brand);
      box-shadow: 0 0 0 3px rgba(20,184,166,0.08);
    }
    .filter-input::placeholder { color: var(--muted); }
    .filter-select {
      padding: 12px 36px 12px 16px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      color: var(--dim); font-family: var(--sans); font-size: 13px;
      outline: none; appearance: none; cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%235e586a' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 14px center;
      transition: border-color 200ms var(--ease-out);
    }
    .filter-select:focus { border-color: var(--brand); }

    /* ── Job Cards ── */
    .job-list { display: flex; flex-direction: column; gap: 6px; }
    .job-card {
      padding: 22px 24px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      cursor: pointer; position: relative; overflow: hidden;
      transition: all 200ms var(--ease-out);
    }
    .job-card::before {
      content: ''; position: absolute; left: 0; top: 0; bottom: 0;
      width: 3px; background: var(--brand); opacity: 0;
      transition: opacity 200ms var(--ease-out);
    }
    .job-card:hover {
      border-color: var(--border-mid);
      background: var(--surface2);
    }
    .job-card:hover::before { opacity: 1; }
    .job-card:active { transform: scale(0.998); }
    .job-card.expanded { border-color: var(--border-mid); background: var(--surface); }
    .job-card.expanded::before { opacity: 1; }
    .job-card-header {
      display: flex; align-items: flex-start; gap: 12px; cursor: pointer;
    }
    .job-card-left { flex: 1; min-width: 0; }
    .job-chevron {
      font-family: var(--mono); font-size: 14px; color: var(--muted);
      transition: transform 250ms var(--ease-out); flex-shrink: 0;
      margin-top: 4px;
    }
    .job-chevron.open { transform: rotate(90deg); }
    .job-expanded {
      margin-top: 20px; padding-top: 20px;
      border-top: 1px solid var(--border);
      animation: fadeIn 250ms var(--ease-out);
    }

    /* ── Powered by Beskar (per card) ── */
    .job-powered {
      font-family: var(--mono); font-size: 10px; letter-spacing: 0.08em;
      color: var(--muted); margin-top: 14px; padding-top: 12px;
      border-top: 1px solid var(--border);
    }
    .job-powered a { color: var(--dim); text-decoration: none; font-weight: 500; }
    .job-powered a:hover { color: var(--brand); }

    /* ── Beta Job ── */
    .job-beta {
      position: relative; overflow: visible;
      border-color: transparent;
      background: var(--surface);
      z-index: 1;
      box-shadow:
        0 0 20px 4px rgba(20,184,166,0.08),
        0 0 50px 8px rgba(167,139,250,0.06),
        0 0 80px 12px rgba(238,178,244,0.04);
      animation: betaPulse 4s ease-in-out infinite;
    }
    .job-beta:hover {
      box-shadow:
        0 0 24px 6px rgba(20,184,166,0.12),
        0 0 60px 12px rgba(167,139,250,0.08),
        0 0 100px 16px rgba(238,178,244,0.06);
    }
    @keyframes betaPulse {
      0%, 100% {
        box-shadow:
          0 0 20px 4px rgba(20,184,166,0.06),
          0 0 50px 8px rgba(167,139,250,0.04),
          0 0 80px 12px rgba(238,178,244,0.02);
      }
      50% {
        box-shadow:
          0 0 28px 6px rgba(20,184,166,0.1),
          0 0 60px 10px rgba(167,139,250,0.07),
          0 0 100px 16px rgba(238,178,244,0.05);
      }
    }
    .job-beta::before {
      content: ''; position: absolute; inset: -2px; border-radius: calc(var(--radius) + 2px);
      background: conic-gradient(
        from var(--beta-angle, 0deg),
        #14b8a6, #a78bfa, #eeb2f4, #14b8a6, #a78bfa, #eeb2f4, #14b8a6
      );
      z-index: -2;
      animation: betaSpin 4s linear infinite;
      opacity: 0.6;
    }
    .job-beta::after {
      content: ''; position: absolute; inset: 0; border-radius: var(--radius);
      background: var(--surface);
      z-index: -1;
    }
    .job-beta:hover::before { opacity: 1; }
    .beta-badge {
      position: absolute; top: 14px; right: 44px;
      font-family: var(--mono); font-size: 9px; font-weight: 700;
      letter-spacing: 0.14em; color: #fff;
      padding: 4px 12px; border-radius: 4px;
      background: linear-gradient(135deg, #14b8a6, #a78bfa, #eeb2f4);
      background-size: 200% 200%;
      animation: betaBadgeShift 3s ease-in-out infinite;
      box-shadow: 0 0 16px rgba(167,139,250,0.3), 0 0 32px rgba(20,184,166,0.15);
    }
    @keyframes betaSpin {
      to { --beta-angle: 360deg; }
    }
    @keyframes betaBadgeShift {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    @property --beta-angle {
      syntax: '<angle>';
      initial-value: 0deg;
      inherits: false;
    }
    .job-title {
      font-family: var(--display); font-size: 16px; font-weight: 700;
      color: var(--text); letter-spacing: -0.02em; margin-bottom: 6px;
    }
    .job-meta {
      display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
      margin-bottom: 10px;
    }
    .job-meta-item {
      font-family: var(--mono); font-size: 11px; color: var(--dim);
      letter-spacing: 0.02em;
    }
    .job-meta-sep { color: var(--muted); font-size: 10px; }
    .job-skills {
      display: flex; flex-wrap: wrap; gap: 5px;
    }
    .skill-tag {
      font-family: var(--mono); font-size: 10px; padding: 3px 10px;
      border-radius: 3px; background: rgba(20,184,166,0.05);
      border: 1px solid rgba(20,184,166,0.1); color: var(--teal);
      letter-spacing: 0.03em; transition: all 150ms var(--ease-out);
    }
    .job-card:hover .skill-tag {
      background: rgba(20,184,166,0.08);
      border-color: rgba(20,184,166,0.15);
    }
    .clearance-badge {
      font-family: var(--mono); font-size: 9px; padding: 3px 10px;
      border-radius: 3px; background: rgba(192,132,252,0.06);
      border: 1px solid rgba(192,132,252,0.15); color: var(--pink);
      letter-spacing: 0.08em; font-weight: 700; text-transform: uppercase;
    }
    .salary {
      font-family: var(--mono); font-size: 11px; color: var(--green);
      letter-spacing: 0.02em;
    }
    .job-count {
      font-family: var(--mono); font-size: 10px; color: var(--muted);
      letter-spacing: 0.08em; text-transform: uppercase;
      margin-bottom: 16px;
    }

    /* ── Job Detail ── */
    .detail { animation: fadeIn 250ms var(--ease-out); }
    .detail-back {
      font-family: var(--mono); font-size: 11px; color: var(--dim);
      cursor: pointer; display: inline-flex; align-items: center;
      gap: 6px; transition: color 150ms; letter-spacing: 0.04em;
      margin-bottom: 24px; padding: 6px 0;
    }
    .detail-back:hover { color: var(--teal); }
    .detail-header {
      margin-bottom: 28px;
    }
    .detail-title {
      font-family: var(--display); font-size: 28px; font-weight: 900;
      letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 12px;
    }
    .detail-meta {
      display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
      margin-bottom: 16px;
    }
    .detail-description {
      font-size: 14px; line-height: 1.8; color: var(--dim);
      padding: 28px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      margin-bottom: 28px;
    }
    .detail-description h1, .detail-description h2, .detail-description h3 {
      color: var(--text); font-family: var(--display); font-weight: 700;
      margin: 20px 0 10px; font-size: 15px; letter-spacing: -0.01em;
    }
    .detail-description h1:first-child, .detail-description h2:first-child {
      margin-top: 0;
    }
    .detail-description ul, .detail-description ol {
      padding-left: 20px; margin: 10px 0;
    }
    .detail-description li {
      margin-bottom: 6px; color: var(--dim);
    }
    .detail-description li::marker { color: var(--muted); }
    .detail-description strong { color: var(--text); font-weight: 600; }
    .detail-description a { color: var(--teal); text-decoration: none; }
    .detail-apply-btn {
      width: 100%; padding: 16px; background: var(--brand);
      border: none; border-radius: var(--radius); color: #0a0c10;
      font-family: var(--display); font-size: 15px; font-weight: 800;
      cursor: pointer; letter-spacing: -0.01em;
      transition: all 180ms var(--ease-out);
      position: relative; overflow: hidden;
    }
    .detail-apply-btn:hover {
      filter: brightness(1.12);
      box-shadow: 0 4px 24px rgba(20,184,166,0.2);
    }
    .detail-apply-btn:active { transform: scale(0.985); }

    /* ── Apply Form ── */
    .apply-form { animation: fadeIn 250ms var(--ease-out); }
    .apply-title {
      font-family: var(--display); font-size: 22px; font-weight: 800;
      letter-spacing: -0.03em; margin: 20px 0 24px;
    }
    .apply-subtitle {
      font-family: var(--mono); font-size: 10px; color: var(--muted);
      letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 20px;
    }
    .form-group { margin-bottom: 16px; }
    .form-row {
      display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
    }
    .form-label {
      display: block; font-family: var(--mono); font-size: 10px;
      letter-spacing: 0.1em; color: var(--muted); text-transform: uppercase;
      margin-bottom: 6px; font-weight: 500;
    }
    .form-input {
      width: 100%; padding: 12px 16px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      color: var(--text); font-family: var(--sans); font-size: 14px;
      outline: none; transition: all 200ms var(--ease-out);
    }
    .form-input:focus {
      border-color: var(--brand);
      box-shadow: 0 0 0 3px rgba(20,184,166,0.08);
    }
    .form-input::placeholder { color: var(--muted); }
    .form-select {
      width: 100%; padding: 12px 16px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      color: var(--dim); font-family: var(--sans); font-size: 14px;
      outline: none; appearance: none; cursor: pointer;
    }
    .form-consent {
      display: flex; align-items: flex-start; gap: 12px;
      padding: 16px 18px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      cursor: pointer; margin: 20px 0;
      transition: border-color 150ms var(--ease-out);
    }
    .form-consent:hover { border-color: var(--border-mid); }
    .form-consent input {
      accent-color: var(--teal); width: 16px; height: 16px;
      margin-top: 2px; flex-shrink: 0; cursor: pointer;
    }
    .form-consent-text {
      font-size: 12px; color: var(--dim); line-height: 1.6;
    }
    .form-submit {
      width: 100%; padding: 16px; background: var(--brand);
      border: none; border-radius: var(--radius); color: #0a0c10;
      font-family: var(--display); font-size: 15px; font-weight: 800;
      cursor: pointer; letter-spacing: -0.01em;
      transition: all 180ms var(--ease-out);
    }
    .form-submit:hover {
      filter: brightness(1.12);
      box-shadow: 0 4px 24px rgba(20,184,166,0.2);
    }
    .form-submit:active { transform: scale(0.985); }
    .form-submit:disabled {
      opacity: 0.4; cursor: not-allowed; filter: none;
      transform: none; box-shadow: none;
    }
    .form-error {
      font-family: var(--mono); font-size: 11px; color: var(--red);
      margin-top: 8px; letter-spacing: 0.02em;
    }

    /* ── Resume Drop ── */
    .resume-drop {
      transition: border-color 200ms var(--ease-out), color 200ms var(--ease-out);
    }
    .resume-drop:hover, .resume-drop.drag-over {
      border-color: var(--brand) !important;
      color: var(--brand) !important;
    }

    /* ── Verification Feed ── */
    .verify-feed {
      padding: 32px 0; animation: fadeIn 300ms var(--ease-out);
    }
    .verify-title {
      font-family: var(--mono); font-size: 10px; letter-spacing: 0.12em;
      color: var(--muted); text-transform: uppercase; margin-bottom: 20px;
    }
    .verify-step {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 0; font-size: 13px;
      animation: slideIn 350ms var(--ease-out) both;
    }
    .verify-step:nth-child(1) { animation-delay: 0ms; }
    .verify-step:nth-child(2) { animation-delay: 100ms; }
    .verify-step:nth-child(3) { animation-delay: 200ms; }
    .verify-step:nth-child(4) { animation-delay: 300ms; }
    .verify-step:nth-child(5) { animation-delay: 400ms; }
    .verify-step:nth-child(6) { animation-delay: 500ms; }
    .verify-step:nth-child(7) { animation-delay: 600ms; }
    .verify-step:nth-child(8) { animation-delay: 700ms; }
    .verify-icon {
      width: 22px; height: 22px; display: flex;
      align-items: center; justify-content: center;
      flex-shrink: 0; font-size: 11px; border-radius: 50%;
    }
    .verify-icon.found {
      color: var(--green); background: rgba(52,211,153,0.08);
      border: 1px solid rgba(52,211,153,0.15);
    }
    .verify-icon.running {
      color: var(--brand); background: rgba(20,184,166,0.08);
      border: 1px solid rgba(20,184,166,0.15);
      animation: pulse 1.5s ease-in-out infinite;
    }
    .verify-icon.not-found {
      color: var(--muted); background: rgba(94,88,106,0.08);
      border: 1px solid rgba(94,88,106,0.15);
    }
    .verify-label { color: var(--text); font-size: 13px; }
    .verify-detail {
      color: var(--dim); font-family: var(--mono);
      font-size: 10px; margin-left: auto; letter-spacing: 0.02em;
    }

    /* ── Complete ── */
    .complete {
      text-align: center; padding: 64px 0;
      animation: fadeIn 400ms var(--ease-out);
    }
    .complete-hex {
      width: 64px; height: 64px; margin: 0 auto 20px;
      display: flex; align-items: center; justify-content: center;
      font-size: 28px; border-radius: 50%;
      background: rgba(20,184,166,0.06);
      border: 1px solid rgba(20,184,166,0.15);
      animation: scaleIn 500ms var(--ease-bounce);
    }
    .complete-title {
      font-family: var(--display); font-size: 22px; font-weight: 800;
      letter-spacing: -0.03em; margin-bottom: 6px;
    }
    .complete-sub {
      color: var(--dim); font-size: 14px; margin-bottom: 16px;
    }
    .complete-cta {
      display: inline-flex; align-items: center; gap: 6px;
      margin: 8px auto 6px; padding: 12px 28px;
      background: var(--brand); color: #0a0c10;
      border-radius: 6px; text-decoration: none;
      font-family: var(--display); font-size: 14px; font-weight: 700;
      letter-spacing: 0.01em;
      transition: transform 120ms var(--ease-out), opacity 150ms var(--ease-out);
    }
    .complete-cta:hover { opacity: 0.92; }
    .complete-cta:active { transform: scale(0.97); }
    .complete-cta-sub {
      max-width: 440px; margin: 8px auto 0;
      color: var(--dim); font-size: 12px; line-height: 1.6;
    }
    .complete-tier {
      font-family: var(--mono); font-size: 11px; letter-spacing: 0.1em;
      padding: 6px 18px; border-radius: 4px; display: inline-block;
      font-weight: 700; text-transform: uppercase;
    }
    .tier-verified { background: rgba(192,132,252,0.08); color: var(--pink); border: 1px solid rgba(192,132,252,0.2); }
    .tier-strongly { background: rgba(20,184,166,0.08); color: var(--teal); border: 1px solid rgba(20,184,166,0.2); }
    .tier-partial { background: rgba(52,211,153,0.08); color: var(--green); border: 1px solid rgba(52,211,153,0.2); }
    .tier-limited { background: rgba(251,191,36,0.08); color: var(--amber); border: 1px solid rgba(251,191,36,0.2); }
    .tier-insufficient { background: rgba(94,88,106,0.06); color: var(--muted); border: 1px solid rgba(94,88,106,0.15); }

    /* ── Gap Questions ── */
    .gap-questions {
      margin-top: 28px; padding: 24px; background: var(--surface);
      border: 1px solid var(--border); border-radius: var(--radius);
      animation: fadeIn 300ms var(--ease-out);
    }
    .gap-title {
      font-family: var(--mono); font-size: 10px; letter-spacing: 0.12em;
      color: var(--dim); text-transform: uppercase; margin-bottom: 16px;
    }

    /* ── Footer ── */
    .footer {
      text-align: center; padding: 24px 0; margin-top: 40px;
      border-top: 1px solid var(--border);
    }
    .footer-text {
      font-family: var(--mono); font-size: 9px;
      letter-spacing: 0.12em; color: var(--muted);
    }
    .footer a { color: var(--muted); text-decoration: none; }
    .footer a:hover { color: var(--dim); }

    /* ── Empty State ── */
    .empty {
      padding: 64px 24px; text-align: center; color: var(--dim);
      font-size: 14px;
    }
    .empty-icon {
      font-size: 32px; margin-bottom: 12px; opacity: 0.3;
    }

    /* ── Error ── */
    .error {
      padding: 64px 24px; text-align: center; color: var(--dim);
    }
    .error-title {
      font-family: var(--display); font-size: 16px; font-weight: 700;
      color: var(--text); margin-bottom: 8px;
    }

    /* ── Animations ── */
    @keyframes fadeIn {
      from { opacity: 0; } to { opacity: 1; }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    /* ── Responsive ── */
    @media (max-width: 600px) {
      .wrapper { padding: 24px 16px; }
      .filters { flex-direction: column; }
      .form-row { grid-template-columns: 1fr; }
      .job-card { padding: 16px 18px; }
      .detail-title { font-size: 22px; }
      .detail-description { padding: 20px; }
    }
  `;
}
